import { FC, useMemo, useState } from 'react';
import { Button, Col, Divider, Input, Row, Space, Typography } from 'antd';
import { Plus, Tag as TagIcon, Tags } from 'lucide-react';
import { TbCrosshair, TbEye, TbSearch } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useTags, useWatchlists } from '@/queries';
import { EmptySection, VirtualizedList as List, SkeletonList } from '@/shared';
import { Tag as WorkspaceTag } from '@/types';

import PrimitiveEditorModal from './components/PrimitiveEditorModal';
import PrimitiveListItem from './components/PrimitiveListItem';
import { isWorkspaceTag, PrimitiveKind, PrimitiveRecord } from './types';

const EMPTY_TAGS: WorkspaceTag[] = [];

const PrimitiveManager: FC<{ kind: PrimitiveKind }> = ({ kind }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const tagQuery = useTags();
    const watchlistQuery = useWatchlists();
    const [search, setSearch] = useState('');
    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState<PrimitiveRecord>();
    const [openTagIds, setOpenTagIds] = useState<Set<string>>(new Set());
    const [parent, setParent] = useState<WorkspaceTag>();
    const tags = tagQuery.data ?? EMPTY_TAGS;
    const watchlists = watchlistQuery.data ?? [];
    const records: PrimitiveRecord[] = kind === 'tags' ? tags : watchlists;
    const loading = kind === 'tags' ? tagQuery.isLoading : watchlistQuery.isLoading;

    const filteredRecords = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        return normalizedSearch
            ? records.filter((record) =>
                  (isWorkspaceTag(record) ? record.name : record.title).toLowerCase().includes(normalizedSearch),
              )
            : records;
    }, [records, search]);
    const tagGroups = useMemo(() => {
        if (kind !== 'tags') {
            return [];
        }

        const normalizedSearch = search.trim().toLowerCase();
        const parentTags = tags.filter((tag) => !tag.parentId);
        const knownParentIds = new Set(parentTags.map((tag) => tag.id));
        const groups = parentTags.flatMap((parentTag) => {
            const children = tags.filter((tag) => tag.parentId === parentTag.id);
            const parentMatches = parentTag.name.toLowerCase().includes(normalizedSearch);
            const matchingChildren = normalizedSearch
                ? children.filter((tag) => tag.name.toLowerCase().includes(normalizedSearch))
                : children;

            return !normalizedSearch || parentMatches || matchingChildren.length
                ? [{ children: parentMatches ? children : matchingChildren, parent: parentTag }]
                : [];
        });
        const orphans = tags.filter(
            (tag) =>
                tag.parentId &&
                !knownParentIds.has(tag.parentId) &&
                (!normalizedSearch || tag.name.toLowerCase().includes(normalizedSearch)),
        );

        return [...groups, ...orphans.map((tag) => ({ children: [], parent: tag }))];
    }, [kind, search, tags]);
    const hasRecords = kind === 'tags' ? tagGroups.length > 0 : filteredRecords.length > 0;

    const openEditor = (record?: PrimitiveRecord, parentTag?: WorkspaceTag) => {
        setEditing(record);
        setParent(parentTag);
        setEditorOpen(true);
    };

    const singular = kind === 'tags' ? 'Tag' : 'Watchlist';
    const plural = kind === 'tags' ? 'Tags' : 'Watchlists';
    const createIcon =
        kind === 'tags' ? (
            <Space size={2}>
                <TagIcon size='0.875rem' />
                <Plus size='0.75rem' />
            </Space>
        ) : (
            <TbEye size='1rem' />
        );
    const headingIcon = kind === 'tags' ? <Tags size='1rem' /> : <TbCrosshair size='1rem' />;

    return (
        <>
            <Typography.Title level={4}>
                <span style={{ marginRight: '0.5rem', verticalAlign: '-0.125rem' }}>{headingIcon}</span>
                {plural}
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                {kind === 'tags'
                    ? 'Organize companies and workspace resources with reusable tags and one level of subtags.'
                    : 'Track companies and workspace resources in focused, permission-aware watchlists.'}
            </Typography.Paragraph>
            <Divider style={{ margin: '1rem 0' }} />
            <Row gutter={[16, 0]}>
                <Col flex='auto'>
                    <Input
                        placeholder={`Search ${plural}...`}
                        prefix={<TbSearch style={{ color: token.colorTextQuaternary, marginRight: '0.325rem' }} />}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </Col>
                <Col flex='none'>
                    <Button icon={createIcon} type='primary' onClick={() => openEditor()}>
                        Create {singular}
                    </Button>
                </Col>
            </Row>
            <Divider style={{ marginBottom: 0, marginTop: '1rem' }} />
            {loading ? (
                <SkeletonList listLength={6} maxHeight='20rem' minHeight='20rem' />
            ) : !hasRecords ? (
                <EmptySection
                    description={
                        search
                            ? `No ${plural.toLowerCase()} match “${search.trim()}”.`
                            : `Create your first ${singular.toLowerCase()} to organize workspace intelligence.`
                    }
                    extra={
                        search ? (
                            <Button size='small' onClick={() => setSearch('')}>
                                Clear Search
                            </Button>
                        ) : undefined
                    }
                    height='20rem'
                    icon={headingIcon}
                    label={`No ${plural} Found`}
                />
            ) : (
                <>
                    {kind === 'tags' ? (
                        <div>
                            {tagGroups.map(({ children, parent: parentTag }) => (
                                <PrimitiveListItem
                                    key={parentTag.id}
                                    kind={kind}
                                    open={Boolean(search.trim()) || openTagIds.has(parentTag.id)}
                                    record={parentTag}
                                    onAddSubtag={(tag) => openEditor(undefined, tag)}
                                    onEdit={(item) => openEditor(item)}
                                    onOpenChange={(open) =>
                                        setOpenTagIds((current) => {
                                            const next = new Set(current);

                                            if (open) {
                                                next.add(parentTag.id);
                                            } else {
                                                next.delete(parentTag.id);
                                            }

                                            return next;
                                        })
                                    }
                                >
                                    {children.length
                                        ? children.map((child) => (
                                              <PrimitiveListItem
                                                  key={child.id}
                                                  kind={kind}
                                                  nested
                                                  record={child}
                                                  onAddSubtag={(tag) => openEditor(undefined, tag)}
                                                  onEdit={(item) => openEditor(item)}
                                              />
                                          ))
                                        : undefined}
                                </PrimitiveListItem>
                            ))}
                        </div>
                    ) : (
                        <List
                            dataSource={filteredRecords}
                            renderItem={(record) => (
                                <PrimitiveListItem
                                    key={record.id}
                                    kind={kind}
                                    record={record}
                                    onAddSubtag={(tag) => openEditor(undefined, tag)}
                                    onEdit={(item) => openEditor(item)}
                                />
                            )}
                            size='small'
                        />
                    )}
                </>
            )}
            {editorOpen ? (
                <PrimitiveEditorModal
                    initialParentId={parent?.id}
                    kind={kind}
                    open
                    record={editing}
                    tags={tags}
                    onClose={() => {
                        setEditorOpen(false);
                        setEditing(undefined);
                        setParent(undefined);
                    }}
                />
            ) : undefined}
        </>
    );
};

export default PrimitiveManager;
